import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, History, ChevronDown } from "lucide-react";

/**
 * Choosing questions from the bank.
 *
 * Shared by the worksheet editor and anywhere else questions get picked, so
 * there is one idea of what the list looks like. Questions are identified by
 * their short code throughout - the bank reassigns ids whenever a paper is
 * re-extracted, so anything holding ids would quietly empty itself.
 */
/**
 * Foundation or Higher.
 *
 * Worth a badge of its own rather than leaving it buried in "1MA1/1F", where
 * the only thing separating a Foundation question from a Higher one is the
 * last character of a paper code. A tutor building a Foundation paper needs
 * to see at a glance that nothing Higher has crept into it.
 */
export function Tier({ value }) {
  if (!value) return null;
  const higher = value === "Higher";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
      higher ? "text-violet-700 border-violet-200 bg-violet-50"
             : "text-sky-700 border-sky-200 bg-sky-50"}`}>
      {higher ? "Higher" : "Foundation"}
    </span>
  );
}

export function Band({ value }) {
  if (!value) return null;
  const cls = {
    bronze: "text-amber-800 border-amber-200 bg-amber-50",
    silver: "text-slate-700 border-slate-300 bg-slate-50",
    gold: "text-yellow-800 border-yellow-300 bg-yellow-50",
  }[value] || "text-slate-600 border-slate-200";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>{value}</span>;
}

/**
 * Does this look like somebody typing question codes rather than words?
 *
 * Codes are three characters, a dash, three characters - `KBV-2YX`. Typed by
 * a person reading them off a printed sheet, so the dash goes missing, the
 * case is whatever the keyboard was doing, and several arrive at once
 * separated by spaces or commas. All of that is accepted and tidied, because
 * a search box that only works when the input is perfect is a search box
 * people stop using.
 *
 * Anything that is not a clean list of codes is treated as words, so
 * searching for "prism" still searches the wording.
 */
const CODE = /^[A-Za-z0-9]{3}-?[A-Za-z0-9]{3}$/;

export function readCodes(text) {
  const parts = String(text || "").trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0 || !parts.every((x) => CODE.test(x))) return null;
  return parts.map((x) => {
    const bare = x.replace(/-/g, "").toUpperCase();
    return `${bare.slice(0, 3)}-${bare.slice(3)}`;
  });
}

/**
 * How often this question has already gone out, and where.
 *
 * A badge, not a barrier. The tutor was explicit - "so that I don't repeat
 * things unless I have to, I should be flexible" - so a question that has
 * been set before is marked and remains entirely pickable. Setting the same
 * question again is often the point: it is how you find out whether it stuck.
 *
 * Silent when a question has never been given, because a badge on every row
 * saying "0" is a badge nobody reads.
 */
function GivenBadge({ history, open, onOpen }) {
  const given = history?.given || 0;
  const planned = (history?.worksheets || []).length;
  if (!history || (given === 0 && planned === 0)) return null;

  // Never issued but sitting in a worksheet is a different fact from having
  // been set, and the amber is reserved for the one that reached a child.
  const only_planned = given === 0;
  return (
    <button type="button"
      onClick={(ev) => { ev.stopPropagation(); onOpen(); }}
      title={only_planned ? "In a worksheet, not yet given out" : "Where this has been set before"}
      className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${
        only_planned
          ? "text-slate-600 border-slate-300 bg-slate-50 hover:bg-slate-100"
          : "text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100"}`}>
      <History size={11} />
      {only_planned ? "in a worksheet" : `given ${given}×`}
      <ChevronDown size={11} className={open ? "rotate-180 transition" : "transition"} />
    </button>
  );
}


/** The expanded "where has this been" list for one question. */
function GivenDetail({ history }) {
  const assignments = history?.assignments || [];
  const worksheets = history?.worksheets || [];
  const pretty = (d) => (d
    ? new Date(d + "T12:00:00").toLocaleDateString("en-GB",
        { day: "numeric", month: "short", year: "numeric" })
    : "no date");

  return (
    <div className="px-3 pb-3 pt-0.5 bg-slate-50/80 border-t border-slate-100">
      {assignments.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            Given to children
          </p>
          <ul className="space-y-0.5 mb-2">
            {assignments.map((a) => (
              <li key={a.id} className="text-xs text-slate-700 flex flex-wrap gap-x-2">
                <span className="font-medium">{a.title}</span>
                <span className="capitalize text-slate-500">{a.kind}</span>
                <span className="text-slate-500">{pretty(a.date)}</span>
                <span className="text-slate-500">
                  {a.students} student{a.students === 1 ? "" : "s"}
                </span>
                {a.programme_day ? (
                  <span className="text-slate-500">day {a.programme_day}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
      {worksheets.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            In these worksheets
          </p>
          <ul className="space-y-0.5">
            {worksheets.map((w) => (
              <li key={w.id} className="text-xs text-slate-700 flex flex-wrap gap-x-2">
                <span className="font-medium">{w.title}</span>
                <span className="capitalize text-slate-500">{w.kind}</span>
                {w.archived ? <span className="text-slate-400">archived</span> : null}
              </li>
            ))}
          </ul>
        </>
      )}
      {assignments.length === 0 && worksheets.length === 0 && (
        <p className="text-xs text-slate-500">Never used.</p>
      )}
    </div>
  );
}


export default function QuestionPicker({ chosen, onToggle }) {
  const [topics, setTopics] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [calculator, setCalculator] = useState("");
  const [tier, setTier] = useState("");
  const [history, setHistory] = useState({});
  const [openCode, setOpenCode] = useState(null);

  useEffect(() => {
    api.get("/admin/bank/topics").then((r) => setTopics(r.data || [])).catch(() => setTopics([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 25 };
    const asCodes = readCodes(search);
    if (asCodes) {
      params.codes = asCodes.join(",");
      // A code is an exact thing. Keeping the band and topic filters on top
      // of it means pasting a code and being told there is no such question,
      // when really it just is not silver.
      params.limit = Math.max(25, asCodes.length);
    } else if (search.trim()) {
      params.q = search.trim();
    }
    if (topic) params.topic = topic;
    if (difficulty) params.difficulty = difficulty;
    if (calculator) params.calculator = calculator;
    if (tier) params.tier = tier;
    api.get("/admin/bank/questions", { params })
      .then((r) => {
        const got = r.data.questions || [];
        setRows(got);
        setTotal(r.data.total || 0);
        setOpenCode(null);
        const codes = got.map((x) => x.code).filter(Boolean);
        if (codes.length === 0) { setHistory({}); return; }
        // One call for the whole page, not one per row. If it fails the list
        // still works - a missing badge is better than a blocked picker.
        api.get("/admin/question-history", { params: { codes: codes.join(",") } })
          .then((h) => setHistory(h.data || {}))
          .catch(() => setHistory({}));
      })
      .catch(() => toast.error("Could not load questions"))
      .finally(() => setLoading(false));
  }, [search, topic, difficulty, calculator, tier]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search wording, or paste codes like KBV-2YX"
            className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={topic} onChange={(e) => setTopic(e.target.value)}
          className="border border-slate-300 rounded-lg px-2 py-2 text-sm max-w-[200px]">
          <option value="">All topics</option>
          {topics.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={tier} onChange={(e) => setTier(e.target.value)}
          className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
          <option value="">Any tier</option>
          <option value="Foundation">Foundation</option>
          <option value="Higher">Higher</option>
        </select>
        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}
          className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
          <option value="">Any band</option>
          <option value="bronze">Bronze</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
        </select>
        <select value={calculator} onChange={(e) => setCalculator(e.target.value)}
          className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
          <option value="">Calc: any</option>
          <option value="no">Non-calc</option>
          <option value="yes">Calculator</option>
        </select>
      </div>

      <p className="text-xs text-slate-500 mb-2">
        {loading ? "Loading…" : `${total.toLocaleString("en-GB")} match`} · showing {rows.length}
      </p>

      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
        {rows.map((r) => {
          const on = chosen.includes(r.code);
          const h = history[r.code];
          return (
            <div key={r.code}>
              <button type="button" onClick={() => onToggle(r.code)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 ${on ? "bg-blue-50/60" : ""}`}>
                <input type="checkbox" checked={on} readOnly className="pointer-events-none" />
                <span className="font-mono text-xs font-semibold w-[76px] shrink-0">{r.code}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{r.topic || "No topic"}</span>
                  <span className="block text-xs text-slate-500">
                    {r.paper_code} · {r.series} {r.year} · Q{r.number}
                    {r.calculator_allowed === false ? " · non-calc" : ""}
                  </span>
                </span>
                <GivenBadge history={h} open={openCode === r.code}
                  onOpen={() => setOpenCode(openCode === r.code ? null : r.code)} />
                <Tier value={r.tier} />
                <Band value={r.difficulty} />
                <span className="text-xs tabular-nums text-slate-500 w-[46px] text-right">{r.marks} mk</span>
              </button>
              {openCode === r.code && <GivenDetail history={h} />}
            </div>
          );
        })}
        {!loading && rows.length === 0 && <p className="px-3 py-8 text-center text-sm text-slate-500">Nothing matches.</p>}
      </div>
    </div>
  );
}
